import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Task } from "../../types/types";
import Header from "../Header/Header";
import BacklogView from "../BacklogView/BacklogView";
import SprintView from "../SprintView/SprintView";
import { LoginUserData } from "../LoginForm/LoginForm";
import {
  ChevronDown,
  Calendar,
  List,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Settings,
} from "lucide-react";
import { realAPI } from "../../services/realAPI";

interface KanbanDashboardProps {
  currentUser?: LoginUserData;
  onLogout?: () => void;
}

interface Team {
  id: string;
  name: string;
  description: string;
  memberIds: string[];
  enabled?: boolean;
}

interface Sprint {
  id: string;
  organizationId: string;
  teamId: string;
  name: string;
  description: string;
  startDate: Date;
  endDate: Date;
  status: "planning" | "active" | "completed";
  createdAt: Date;
  createdBy: string;
}

type TabType = "backlog" | string; // string for sprint IDs

const KanbanDashboard: React.FC<KanbanDashboardProps> = ({
  currentUser,
  onLogout,
}) => {
  const location = useLocation();
  const [userTeams, setUserTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [teamSprints, setTeamSprints] = useState<Sprint[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>("backlog");
  const [isLoading, setIsLoading] = useState(true);
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);
  const [sprintScrollPosition, setSprintScrollPosition] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Organization data state
  const [organizationData, setOrganizationData] = useState<any>(null);

  // Notification preferences - initialize from currentUser
  const [emailNotifications, setEmailNotifications] = useState(
    currentUser?.emailNotifications !== undefined
      ? currentUser.emailNotifications
      : true
  );
  const [browserNotifications, setBrowserNotifications] = useState(true);
  const [notificationPermission, setNotificationPermission] =
    useState<NotificationPermission>("default");

  // Centralized task management - use unified Task interface for both
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [unifiedItems, setUnifiedItems] = useState<Task[]>([]);

  // Notification utility functions
  const requestNotificationPermission = async () => {
    if (!("Notification" in window)) {
      console.warn("This browser does not support notifications");
      return false;
    }

    if (Notification.permission === "granted") {
      setNotificationPermission("granted");
      return true;
    }

    if (Notification.permission !== "denied") {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      return permission === "granted";
    }

    setNotificationPermission("denied");
    return false;
  };

  const showBrowserNotification = useCallback(
    (title: string, options?: NotificationOptions) => {
      console.log("showBrowserNotification called:", { title, options });
      console.log("Current state:", {
        browserNotifications,
        notificationPermission,
      });

      if (!browserNotifications || notificationPermission !== "granted") {
        console.warn("Notification blocked:", {
          browserNotifications,
          notificationPermission,
        });
        return;
      }

      if (!("Notification" in window)) {
        console.warn("Notifications not supported in this browser");
        return;
      }

      try {
        console.log("Creating notification...");
        const notification = new Notification(title, {
          icon: "/favicon.ico",
          badge: "/favicon.ico",
          ...options,
        });

        console.log("Notification created successfully:", notification);

        // Auto-close notification after 5 seconds
        setTimeout(() => {
          console.log("Auto-closing notification");
          notification.close();
        }, 5000);

        return notification;
      } catch (error) {
        console.error("Error showing notification:", error);
        return null;
      }
    },
    [browserNotifications, notificationPermission]
  );

  useEffect(() => {
    const loadUserSettings = async () => {
      if (!currentUser) {
        // No current user, use localStorage
        const savedEmailNotifications =
          localStorage.getItem("emailNotifications");
        const savedBrowserNotifications = localStorage.getItem(
          "browserNotifications"
        );

        if (savedEmailNotifications !== null) {
          setEmailNotifications(JSON.parse(savedEmailNotifications));
        }
        if (savedBrowserNotifications !== null) {
          setBrowserNotifications(JSON.parse(savedBrowserNotifications));
        }
        return;
      }

      try {
        const userSettings = await realAPI.getUserSettings();
        console.log("Loaded user settings:", userSettings);

        if (userSettings) {
          setEmailNotifications(userSettings.emailNotifications ?? true);
          setBrowserNotifications(userSettings.browserNotifications ?? true);
          return;
        }
      } catch (error) {
        console.error("Error loading user settings:", error);
      }

      if (currentUser.emailNotifications !== undefined) {
        setEmailNotifications(currentUser.emailNotifications);
      }

      const savedBrowserNotifications = localStorage.getItem(
        "browserNotifications"
      );
      if (savedBrowserNotifications !== null) {
        setBrowserNotifications(JSON.parse(savedBrowserNotifications));
      }
    };

    loadUserSettings();
  }, [currentUser]);

  const saveNotificationSettings = useCallback(async () => {
    try {
      if (currentUser) {
        await realAPI.updateUserSettings({
          emailNotifications,
          browserNotifications,
        });
      }

      localStorage.setItem(
        "emailNotifications",
        JSON.stringify(emailNotifications)
      );
      localStorage.setItem(
        "browserNotifications",
        JSON.stringify(browserNotifications)
      );

      showBrowserNotification("Preferences saved", {
        body: "Your notification settings have been updated.",
      });

      setIsSettingsOpen(false);
    } catch (error) {
      console.error("Failed to save notification settings:", error);
      alert("Unable to save notification settings. Please try again.");
    }
  }, [
    browserNotifications,
    currentUser,
    emailNotifications,
    showBrowserNotification,
  ]);

  // Load organization data from localStorage
  useEffect(() => {
    const savedOrgData = localStorage.getItem("organizationData");
    if (savedOrgData) {
      try {
        setOrganizationData(JSON.parse(savedOrgData));
      } catch (error) {
        console.error("Error parsing organization data:", error);
      }
    }
  }, []);

  // Initialize notification permission state
  useEffect(() => {
    if ("Notification" in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  // When URL query changes, honor ?team=<id> by switching selected team if available
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const teamParam = params.get("team");
    const tabParam = params.get("tab");
    console.log(`🔗 URL changed: team=${teamParam}, tab=${tabParam}`);

    if (teamParam && userTeams.length) {
      const match = userTeams.find((t) => String(t.id) === String(teamParam));
      if (match && (!selectedTeam || selectedTeam.id !== match.id)) {
        console.log(`🔄 Switching to team: ${match.name} (ID: ${match.id})`);
        setSelectedTeam(match);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, userTeams]);

  // Honor ?tab=backlog or ?tab=<sprintId> from query string
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    console.log(
      `🎯 Tab param from URL: ${tab}, current activeTab: ${activeTab}`
    );
    if (tab && tab !== activeTab) {
      console.log(`🔄 Setting activeTab to: ${tab}`);
      setActiveTab(tab);
    }
  }, [location.search, activeTab]);

  useEffect(() => {
    // Initialize unified items as empty - data will be loaded by BacklogView
    setUnifiedItems([]);
    setAllTasks([]);
    // Backlog items will be fetched inside BacklogView; keep loading until it signals ready
  }, []);

  // Centralized task management functions

  const handleCreateStory = (createdTask: Task) => {
    const updatedItems = [...unifiedItems, createdTask];
    setAllTasks(updatedItems);
    setUnifiedItems(updatedItems);
  };

  const handleCreateBacklogItem = async (newItem: Task): Promise<Task> => {
    try {
      const createdTask = await realAPI.createTask({
        title: newItem.title,
        description: newItem.description,
        priority: newItem.priority,
        taskColumn: newItem.taskColumn || "todo",
        status: newItem.status ?? "backlog",
        teamId: selectedTeam?.id || "",
        assigneeUserId: newItem.assigneeUserId || null,
        storyPoints: newItem.storyPoints,
        type: newItem.type,
        // Pass attachments to the API call
        attachments: newItem.attachments?.map((att) => ({
          originalFilename: att.originalFilename,
          url: att.url,
          thumbnailUrl: att.thumbnailUrl,
        })),
      });
      const updatedItems = [...unifiedItems, createdTask];
      setUnifiedItems(updatedItems);
      setAllTasks(updatedItems);
      return createdTask; // Return the created task with real ID
    } catch (error) {
      console.error("Error creating backlog item:", error);
      throw error; // Re-throw so BacklogView can handle the error
    }
  };

  const handleUpdateBacklogItems = (updatedItems: Task[]) => {
    console.log("KanbanDashboard: Updating unified items:", updatedItems);
    setUnifiedItems(updatedItems);
    setAllTasks(updatedItems);
  };

  const notifyTaskAssignment = useCallback(
    (updatedTask: Task) => {
      if (!currentUser) {
        return;
      }

      if (browserNotifications && notificationPermission === "granted") {
        showBrowserNotification("New Task Assigned", {
          body: `${updatedTask.title} was assigned to you`,
        });
      }

      if (emailNotifications) {
        console.info(
          "Email notification would be sent for task assignment:",
          updatedTask
        );
      }
    },
    [
      browserNotifications,
      currentUser,
      emailNotifications,
      notificationPermission,
      showBrowserNotification,
    ]
  );

  const handleUpdateTask = async (updatedTask: Task) => {
    try {
      // Find the original task to compare assignee changes
      const originalTask = unifiedItems.find(
        (item) => item.id === updatedTask.id
      );

      // Call the API to update both status and column
      await realAPI.updateTaskStatusAndColumn(
        Number(updatedTask.id),
        updatedTask.status ?? "backlog",
        updatedTask.taskColumn ?? "todo"
      );

      // Update local state
      setUnifiedItems((prev) =>
        prev.map((item) => (item.id === updatedTask.id ? updatedTask : item))
      );
      setAllTasks((prev) =>
        prev.map((item) => (item.id === updatedTask.id ? updatedTask : item))
      );

      // Check if task was assigned to current user
      const wasAssignedToCurrentUser =
        updatedTask.assigneeUserId === currentUser?.id &&
        originalTask?.assigneeUserId !== currentUser?.id;

      if (wasAssignedToCurrentUser) {
        notifyTaskAssignment(updatedTask);
      }
    } catch (error) {
      console.error("Error updating task status/column:", error);
    }
  };
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".team-dropdown")) {
        setShowTeamDropdown(false);
      }
    };

    if (showTeamDropdown) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [showTeamDropdown]);

  // Handle settings modal keyboard events
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isSettingsOpen) {
        setIsSettingsOpen(false);
      }
    };

    if (isSettingsOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isSettingsOpen]);

  const loadUserTeams = useCallback(async () => {
    if (!currentUser) return;

    try {
      setIsLoading(true);
      const allTeams: Team[] = await realAPI.getTeamsByOrganization(
        currentUser.organizationId
      );

      console.log("🔍 All teams loaded:", allTeams);
      console.log(
        "🔍 Teams with enabled field:",
        allTeams.map((t) => ({ id: t.id, name: t.name, enabled: t.enabled }))
      );

      const userTeamList = allTeams.filter(
        (team: Team) =>
          // Only include enabled teams (enabled !== false, which handles undefined as enabled)
          team.enabled !== false &&
          (currentUser.teamIds?.includes(String(team.id)) ||
            team.memberIds.map(String).includes(currentUser.id))
      );

      console.log(
        "🔍 Filtered enabled user teams:",
        userTeamList.map((t) => ({
          id: t.id,
          name: t.name,
          enabled: t.enabled,
        }))
      );

      setUserTeams(userTeamList);

      setSelectedTeam((previousTeam) => {
        if (userTeamList.length === 0) {
          return null;
        }

        const params = new URLSearchParams(location.search);
        const teamParam = params.get("team");
        const matched = teamParam
          ? userTeamList.find((t) => String(t.id) === String(teamParam))
          : null;

        if (matched) {
          return matched;
        }

        if (
          !previousTeam ||
          previousTeam.enabled === false ||
          !userTeamList.find((team) => team.id === previousTeam.id)
        ) {
          return userTeamList[0];
        }

        return previousTeam;
      });
    } catch (error) {
      console.error("Error loading user teams:", error);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, location.search]);

  const selectAppropriateTab = useCallback((sprints: Sprint[]) => {
    if (sprints.length === 0) {
      setActiveTab("backlog");
      return;
    }

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0); // Reset time for date comparison

    // Find sprint where current date falls within start and end date
    const currentSprint = sprints.find((sprint) => {
      const startDate = new Date(sprint.startDate);
      const endDate = new Date(sprint.endDate);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      return currentDate >= startDate && currentDate <= endDate;
    });

    if (currentSprint) {
      setActiveTab(currentSprint.id);
      // If current sprint is found,
      return;
    }

    // No current sprint found, find the nearest completed sprint or next planned sprint
    let nearestSprint = sprints[0];
    let minDifference = Math.abs(
      currentDate.getTime() - new Date(sprints[0].startDate).getTime()
    );

    sprints.forEach((sprint) => {
      const sprintStartDate = new Date(sprint.startDate);
      const sprintEndDate = new Date(sprint.endDate);

      // Calculate difference to start date or end date (whichever is closer)
      const startDiff = Math.abs(
        currentDate.getTime() - sprintStartDate.getTime()
      );
      const endDiff = Math.abs(currentDate.getTime() - sprintEndDate.getTime());
      const minSprintDiff = Math.min(startDiff, endDiff);

      if (minSprintDiff < minDifference) {
        minDifference = minSprintDiff;
        nearestSprint = sprint;
      }
    });

    setActiveTab(nearestSprint.id);
  }, []);

  const loadTeamSprints = useCallback(async () => {
    if (!selectedTeam || !currentUser) return;

    try {
      console.log(
        `🔄 Loading sprints for team: ${selectedTeam.name} (ID: ${selectedTeam.id})`
      );
      const allSprints = await realAPI.getSprintsByOrganization(
        currentUser.organizationId
      );
      const sprints = allSprints.filter(
        (sprint: Sprint) => sprint.teamId === selectedTeam.id
      );

      console.log(
        `📊 Found ${sprints.length} sprints for team ${selectedTeam.id}:`,
        sprints.map((s: Sprint) => `${s.name} (${s.id})`)
      );

      // Sort sprints by start date (oldest first)
      const sortedSprints = sprints.sort(
        (a: Sprint, b: Sprint) =>
          new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
      );
      setTeamSprints(sortedSprints);

      // Prefer tab from query param if present
      const params = new URLSearchParams(location.search);
      const tab = params.get("tab");
      if (tab) {
        // If "backlog" is requested, honor it
        if (tab === "backlog") {
          setActiveTab(tab);
        } else {
          // If a specific sprint ID is requested, check if it exists in this team's sprints
          const sprintExists = sortedSprints.find(
            (s: Sprint) => String(s.id) === String(tab)
          );
          if (sprintExists) {
            setActiveTab(tab);
          } else {
            console.warn(
              `Sprint ${tab} not found for team ${selectedTeam.id}, falling back to auto-select`
            );
            // Sprint doesn't exist for this team, auto-select appropriate tab
            selectAppropriateTab(sortedSprints);
          }
        }
      } else {
        // Otherwise, auto-select by date proximity
        selectAppropriateTab(sortedSprints);
      }
    } catch (error) {
      console.error("Error loading team sprints:", error);
    }
  }, [currentUser, location.search, selectAppropriateTab, selectedTeam]);

  useEffect(() => {
    if (currentUser) {
      loadUserTeams();
    } else {
      setIsLoading(false);
    }
  }, [currentUser, loadUserTeams]);

  useEffect(() => {
    if (selectedTeam) {
      loadTeamSprints();
    }
  }, [loadTeamSprints, selectedTeam]);

  const handleTeamChange = (team: Team) => {
    setSelectedTeam(team);
    setShowTeamDropdown(false);
    setActiveTab("backlog"); // Reset to backlog when changing teams
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const getSprintStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "planning":
        return "bg-blue-100 text-blue-800";
      case "completed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const scrollSprints = (direction: "left" | "right") => {
    const container = document.getElementById("sprint-tabs-container");
    if (container) {
      const scrollAmount = 200;
      const newPosition =
        direction === "left"
          ? Math.max(0, sprintScrollPosition - scrollAmount)
          : sprintScrollPosition + scrollAmount;

      container.scrollTo({ left: newPosition, behavior: "smooth" });
      setSprintScrollPosition(newPosition);
    }
  };

  const isCurrentSprint = (sprint: Sprint) => {
    const currentDate = new Date();
    const startDate = new Date(sprint.startDate);
    const endDate = new Date(sprint.endDate);
    currentDate.setHours(0, 0, 0, 0);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    return currentDate >= startDate && currentDate <= endDate;
  };

  useEffect(() => {
    if (teamSprints.length > 0) {
      const currentSprintIndex = teamSprints.findIndex((sprint) =>
        isCurrentSprint(sprint)
      );
      if (currentSprintIndex !== -1) {
        const container = document.getElementById("sprint-tabs-container");
        if (container) {
          const sprintTab = container.children[
            currentSprintIndex
          ] as HTMLElement;
          if (sprintTab) {
            const containerWidth = container.clientWidth;
            const sprintTabWidth = sprintTab.offsetWidth;
            const scrollPosition =
              sprintTab.offsetLeft - containerWidth / 2 + sprintTabWidth / 2;
            container.scrollTo({ left: scrollPosition, behavior: "smooth" });
          }
        }
      }
    }
  }, [teamSprints, activeTab]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 font-sans min-h-screen">
      {/* Enhanced Header with User Info */}
      {currentUser ? (
        <header className="bg-white shadow-sm">
          <div className="container mx-auto px-4 py-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-4">
                  <svg
                    className="w-8 h-8 text-indigo-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 17v-2m3 2v-4m3 4v-6m-9 4h12M3 7h18M5 11h14M5 15h14M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5z"
                    ></path>
                  </svg>
                  <h1 className="text-xl font-bold text-gray-800">
                    Kanban Board
                  </h1>
                </div>

                {/* Organization info - moved to separate row on mobile */}
              </div>

              {/* Right side - User info and mobile menu */}
              <div className="flex items-center space-x-2 md:space-x-4">
                {/* Desktop user welcome */}
                <span className="hidden lg:block text-sm text-gray-600">
                  Welcome, {currentUser.firstName} {currentUser.lastName}!
                </span>

                {/* Mobile menu button */}
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                >
                  {isMobileMenuOpen ? (
                    <X className="h-5 w-5" />
                  ) : (
                    <Menu className="h-5 w-5" />
                  )}
                </button>

                {/* Desktop settings and logout buttons */}
                <div className="hidden md:flex items-center space-x-2">
                  <button
                    onClick={() => setIsSettingsOpen(true)}
                    className="flex items-center space-x-2 text-gray-600 hover:text-indigo-600 transition-colors p-2 rounded-md hover:bg-gray-50"
                    title="Settings"
                  >
                    <Settings className="w-5 h-5" />
                    <span className="hidden lg:block">Settings</span>
                  </button>

                  {onLogout && (
                    <button
                      onClick={onLogout}
                      className="flex items-center space-x-2 text-gray-600 hover:text-indigo-600 transition-colors p-2 rounded-md hover:bg-gray-50"
                      title="Logout"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        ></path>
                      </svg>
                      <span className="hidden lg:block">Logout</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Second row - Organization and Team info */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="px-2 md:px-3 py-1 bg-indigo-100 text-indigo-800 text-xs md:text-sm font-medium rounded-full">
                {organizationData?.name ||
                  currentUser.organization?.name ||
                  "Organization"}
              </span>

              {/* Team Selector */}
              {userTeams.length > 0 &&
                (userTeams.length === 1 ? (
                  <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                    📋 {userTeams[0].name}
                  </span>
                ) : (
                  <div className="relative">
                    <button
                      onClick={() => setShowTeamDropdown(!showTeamDropdown)}
                      className="flex items-center space-x-2 px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full hover:bg-green-200 transition-colors"
                    >
                      <span>📋 {selectedTeam?.name || "Select Team"}</span>
                      <ChevronDown className="w-4 h-4" />
                    </button>

                    {showTeamDropdown && (
                      <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                        {userTeams.map((team) => (
                          <button
                            key={team.id}
                            onClick={() => handleTeamChange(team)}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 first:rounded-t-lg last:rounded-b-lg ${
                              selectedTeam?.id === team.id
                                ? "bg-green-50 text-green-800"
                                : "text-gray-700"
                            }`}
                          >
                            <div className="font-medium">{team.name}</div>
                            <div className="text-xs text-gray-500">
                              {team.description}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

              {userTeams.length === 0 && (
                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full">
                  No Teams Assigned
                </span>
              )}
            </div>

            {/* Mobile Menu Overlay */}
            {isMobileMenuOpen && (
              <div className="md:hidden fixed inset-0 z-50 flex">
                <div
                  className="fixed inset-0 bg-black opacity-50"
                  onClick={() => setIsMobileMenuOpen(false)}
                ></div>
                <div className="relative ml-auto w-64 bg-white shadow-lg h-full">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-gray-900">
                        Menu
                      </h2>
                      <button
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="space-y-4">
                      {/* Mobile organization info */}
                      <div className="py-2 border-b border-gray-200">
                        <div className="text-sm font-medium text-gray-700">
                          Organization
                        </div>
                        <div className="text-lg text-indigo-600">
                          {organizationData?.name ||
                            currentUser.organization?.name ||
                            "Organization"}
                        </div>
                      </div>

                      {/* Mobile user info */}
                      <div className="py-2 border-b border-gray-200">
                        <div className="text-sm font-medium text-gray-700">
                          Logged in as
                        </div>
                        <div className="text-lg">
                          {currentUser.firstName} {currentUser.lastName}
                        </div>
                      </div>

                      {/* Mobile settings button */}
                      <button
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          setIsSettingsOpen(true);
                        }}
                        className="w-full flex items-center space-x-3 px-3 py-3 text-left text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        <Settings className="w-5 h-5" />
                        <span>Settings</span>
                      </button>

                      {/* Mobile logout button */}
                      {onLogout && (
                        <button
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            onLogout();
                          }}
                          className="w-full flex items-center space-x-3 px-3 py-3 text-left text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                            ></path>
                          </svg>
                          <span>Logout</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </header>
      ) : (
        <Header />
      )}

      {/* Team Context Info */}
      {currentUser && selectedTeam && (
        <div className="bg-indigo-50 border-b border-indigo-200">
          <div className="container mx-auto px-4 py-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-sm">
                <span className="text-indigo-600 font-medium">
                  Current Team:
                </span>
                <span className="text-indigo-800">{selectedTeam.name}</span>
                <span className="text-indigo-500">•</span>
                <span className="text-indigo-600">
                  {selectedTeam.description}
                </span>
                <span className="text-indigo-500">•</span>
                <span className="text-indigo-600">
                  {selectedTeam.memberIds.length} members
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sprint Tabs */}
      {currentUser && selectedTeam && (
        <div className="bg-white border-b border-gray-200">
          <div className="container mx-auto px-4">
            <div className="flex items-center">
              {/* Backlog Tab - Always visible */}
              <button
                onClick={() => setActiveTab("backlog")}
                className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors flex-shrink-0 ${
                  activeTab === "backlog"
                    ? "border-indigo-500 text-indigo-600 bg-indigo-50"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <List className="w-4 h-4" />
                <span>Backlog</span>
              </button>

              {/* Sprint Navigation */}
              {teamSprints.length > 0 && (
                <div className="flex items-center flex-1 min-w-0">
                  {/* Left scroll button */}
                  {teamSprints.length > 3 && (
                    <button
                      onClick={() => scrollSprints("left")}
                      className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      disabled={sprintScrollPosition === 0}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                  )}

                  {/* Scrollable Sprint Tabs Container */}
                  <div
                    id="sprint-tabs-container"
                    className="flex space-x-0 overflow-x-auto flex-1"
                    style={{
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                    }}
                    onScroll={(e) =>
                      setSprintScrollPosition(e.currentTarget.scrollLeft)
                    }
                  >
                    {teamSprints.map((sprint) => (
                      <button
                        key={sprint.id}
                        onClick={() => setActiveTab(sprint.id)}
                        className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors flex-shrink-0 ${
                          activeTab === sprint.id
                            ? "border-indigo-500 text-indigo-600 bg-indigo-50"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        }`}
                      >
                        <Calendar className="w-4 h-4" />
                        <span className="flex items-center space-x-2">
                          <span>{sprint.name}</span>
                          {isCurrentSprint(sprint) && (
                            <span
                              className="w-2 h-2 bg-green-500 rounded-full animate-pulse"
                              title="Current Sprint"
                            ></span>
                          )}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${getSprintStatusColor(
                            sprint.status
                          )}`}
                        >
                          {sprint.status}
                        </span>
                        <span className="text-xs text-gray-400">
                          {formatDate(sprint.startDate)} -{" "}
                          {formatDate(sprint.endDate)}
                        </span>
                      </button>
                    ))}
                  </div>

                  {/* Right scroll button */}
                  {teamSprints.length > 3 && (
                    <button
                      onClick={() => scrollSprints("right")}
                      className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}

              {/* No Sprints Message */}
              {teamSprints.length === 0 && (
                <div className="flex items-center px-4 py-3 text-sm text-gray-400">
                  <Calendar className="w-4 h-4 mr-2" />
                  No sprints created yet
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {currentUser && selectedTeam && (
        <div className="bg-gray-50 min-h-screen">
          {activeTab === "backlog" ? (
            <BacklogView
              selectedTeam={selectedTeam}
              sprints={teamSprints}
              backlogItems={unifiedItems} // Pass all items so BacklogView can group by status
              onUpdateBacklogItems={handleUpdateBacklogItems}
              onCreateBacklogItem={handleCreateBacklogItem}
              onUpdateTask={handleUpdateTask}
              onReady={() => {}}
            />
          ) : (
            <div>
              {(() => {
                console.log(
                  `🎯 activeTab type: ${typeof activeTab}, value: "${activeTab}"`
                );
                console.log(
                  `📋 teamSprints:`,
                  teamSprints.map((s: Sprint) => ({
                    id: s.id,
                    type: typeof s.id,
                    name: s.name,
                  }))
                );

                const selectedSprint = teamSprints.find(
                  (s) => String(s.id) === String(activeTab)
                );
                console.log(
                  `🔍 Looking for sprint with ID: ${activeTab}, found:`,
                  selectedSprint?.name || "none"
                );
                return selectedSprint ? (
                  <SprintView
                    sprint={selectedSprint}
                    selectedTeam={selectedTeam}
                    onCreateStory={handleCreateStory}
                    sprintTasks={allTasks}
                    // Pass backend update handler to SprintView's MainContent
                    onUpdateTask={handleUpdateTask}
                  />
                ) : (
                  <div className="text-center py-12">
                    <Calendar className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      Sprint not found
                    </h3>
                    <p className="text-gray-500 text-sm mt-1">
                      Sprint "{activeTab}" not available for team "
                      {selectedTeam?.name}"
                    </p>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Fallback for users without teams or not logged in */}
      {(!currentUser || !selectedTeam) && (
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center max-w-md mx-auto p-8">
            <div className="w-24 h-24 mx-auto mb-6 bg-indigo-100 rounded-full flex items-center justify-center">
              <svg
                className="w-12 h-12 text-indigo-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Welcome to Kanbello!
            </h2>
            <p className="text-gray-600 mb-6">
              You're not currently assigned to any teams. To get started with
              project management and kanban boards, please contact your
              organization admin to be added to a team.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <svg
                  className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <h3 className="text-sm font-medium text-blue-800 mb-1">
                    What happens next?
                  </h3>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Your admin will create teams for your projects</li>
                    <li>• You'll be assigned to one or more teams</li>
                    <li>
                      • You can then access kanban boards and manage tasks
                    </li>
                    <li>• Collaborate with your team members on projects</li>
                  </ul>
                </div>
              </div>
            </div>
            {currentUser && (
              <div className="text-sm text-gray-500">
                Logged in as:{" "}
                <span className="font-medium">
                  {currentUser.firstName} {currentUser.lastName}
                </span>
                <br />
                Organization:{" "}
                <span className="font-medium">
                  {organizationData?.name ||
                    currentUser.organization?.name ||
                    "Your Organization"}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {isSettingsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setIsSettingsOpen(false)}
          ></div>
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Settings Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  User Settings
                </h2>
                <button
                  onClick={() => setIsSettingsOpen(false)}
                  className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* User Profile Section */}
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Profile Information
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name
                    </label>
                    <div className="text-sm text-gray-900 p-2 bg-gray-50 rounded-md">
                      {currentUser?.firstName} {currentUser?.lastName}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <div className="text-sm text-gray-900 p-2 bg-gray-50 rounded-md">
                      {currentUser?.email}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Organization
                    </label>
                    <div className="text-sm text-gray-900 p-2 bg-gray-50 rounded-md">
                      {organizationData?.name ||
                        currentUser?.organization?.name ||
                        "Organization"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Team Memberships Section */}
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Team Memberships
                </h3>
                <div className="space-y-2">
                  {userTeams.length > 0 ? (
                    userTeams.map((team) => (
                      <div
                        key={team.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
                      >
                        <div>
                          <div className="font-medium text-gray-900">
                            {team.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {team.description}
                          </div>
                        </div>
                        {selectedTeam?.id === team.id && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                            Current
                          </span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-gray-500 p-3 bg-gray-50 rounded-md">
                      No teams assigned
                    </div>
                  )}
                </div>
              </div>

              {/* Preferences Section */}
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Preferences
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">
                        Email Notifications
                      </div>
                      <div className="text-sm text-gray-500">
                        Receive updates about tasks and sprints
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={emailNotifications}
                      onChange={(e) => setEmailNotifications(e.target.checked)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">
                        Browser Notifications
                      </div>
                      <div className="text-sm text-gray-500">
                        Get notified about task assignments{" "}
                        {notificationPermission === "denied" && (
                          <span className="text-red-600">
                            (Permission denied)
                          </span>
                        )}
                        {notificationPermission === "default" && (
                          <span className="text-yellow-600">
                            (Permission needed)
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={
                          browserNotifications &&
                          notificationPermission === "granted"
                        }
                        onChange={async (e) => {
                          if (e.target.checked) {
                            const granted =
                              await requestNotificationPermission();
                            if (granted) {
                              setBrowserNotifications(true);
                            }
                          } else {
                            setBrowserNotifications(false);
                          }
                        }}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      {notificationPermission !== "granted" &&
                        browserNotifications && (
                          <button
                            onClick={requestNotificationPermission}
                            className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded hover:bg-yellow-200"
                          >
                            Enable
                          </button>
                        )}
                      {notificationPermission === "granted" &&
                        browserNotifications && (
                          <button
                            onClick={() => {
                              console.log("Test button clicked!");
                              console.log(
                                "browserNotifications:",
                                browserNotifications
                              );
                              console.log(
                                "notificationPermission:",
                                notificationPermission
                              );
                              console.log(
                                "Notification support:",
                                "Notification" in window
                              );

                              if (!browserNotifications) {
                                console.warn(
                                  "Browser notifications are disabled in settings"
                                );
                                alert(
                                  "Browser notifications are disabled in settings"
                                );
                                return;
                              }

                              if (notificationPermission !== "granted") {
                                console.warn(
                                  "Notification permission not granted:",
                                  notificationPermission
                                );
                                alert(
                                  `Notification permission is: ${notificationPermission}`
                                );
                                return;
                              }

                              try {
                                console.log("Creating test notification...");
                                const notification = new Notification(
                                  "Test Notification",
                                  {
                                    body: "Browser notifications are working correctly!",
                                    icon: "/favicon.ico",
                                    tag: "test-notification",
                                  }
                                );
                                console.log(
                                  "Notification created:",
                                  notification
                                );

                                // Show visual confirmation
                                alert(
                                  "✅ Notification sent successfully! Check your system notification center if you don't see it on screen."
                                );

                                // Auto-close after 5 seconds
                                setTimeout(() => {
                                  console.log("Auto-closing notification");
                                  notification.close();
                                }, 5000);
                              } catch (error) {
                                console.error(
                                  "Error creating notification:",
                                  error
                                );
                                alert(
                                  "Error creating notification: " +
                                    (error instanceof Error
                                      ? error.message
                                      : "Unknown error")
                                );
                              }
                            }}
                            className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded hover:bg-green-200"
                          >
                            Test
                          </button>
                        )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setIsSettingsOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  onClick={saveNotificationSettings}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KanbanDashboard;
